import { isEqual, omit, partition, pick, sortBy } from 'lodash'
import Big from 'big.js'
import { getISODay } from 'date-fns'
import { Temporal } from 'temporal-polyfill'

import {
  ContiguousDataRecord,
  FilteredEffectiveDatedFields,
  FormValues,
  FormValuesKey,
  InsertRateFormValue,
  ShiftDatedItem
} from '~/work_order/modify_work_order/PageDataProvider'
import {
  fetchBulkTemplateFields,
  WorkOrderBudgetAllocations,
  WorkOrderChangeSet,
  WorkOrderConfigResponse,
  WorkOrderCurrentData,
  WorkOrderEffectiveDatedFields,
  WorkOrderRevisionRequest
} from '~/api/work_order'
import {
  ADDITIONAL_MANAGERS,
  APPROVERS_AND_MANAGERS_SECTION_FIELDS,
  BILL_RATE,
  BUDGET_ALLOCATIONS,
  CALCULATION_PACK,
  CALENDAR_PAY_PROFILE,
  CHARGE_CODE_SECTION_FIELDS,
  DIRECT_HIRE_SECTION_FIELDS,
  DOUBLE_TIME_MARKUP,
  EVENT_BASED_EXPENSES_SECTION_FIELDS,
  EXEMPT_STATUS,
  EXPENSE_APPROVERS,
  FEE_PROFILE_SECTION_FIELDS,
  FEE_PROFILES,
  GENERAL_SECTION_FIELDS,
  GENERIC_FIELDS_SECTION_FIELDS,
  LOCATION,
  LOCATION_SECTION_FIELDS,
  LOCATIONS,
  MARKUP,
  MIN_HOURS,
  NOT_QUALIFIED_RATE,
  ORG_UNIT_SECTION_FIELDS,
  OVERTIME_MARKUP,
  OVERTIME_PROFILE,
  PAY_PARITY_RATE,
  PAY_RATE,
  PROGRAM_TEAM,
  RATE_SECTION_FIELDS,
  RATE_SECTION_SINGLE_VALUE_FIELDS,
  RATES,
  SCHEDULED_BILLING_SECTION_FIELDS,
  SHIFT_DIFFERENTIAL_MAPPING,
  SHIFTS,
  TIMEKEEPER,
  TIMESHEET_APPROVERS,
  WORK_HOURS_PER_DAY,
  WORK_TYPE_OVERRIDES,
  WORK_TYPE_PROFILE,
  WORK_TYPE_PROFILES,
  WORK_TYPE_SECTION_FIELDS
} from '~/work_order/modify_work_order/constants'
import { DateString } from '~/common/types'
import { ShiftApiData, ShiftDifferentialEnum, ShiftStrategyEnum } from '~/api/shifts'
import { forceArray } from '~/common/arrayUtils'
import { safeParse } from '~/common/dateUtils'
import { ChargeCodeRawValues } from '~/charge_codes/types'
import { toChangeSetData as taxOverrideToChangeSetData } from '~/work_order/tax-overrides/utils'
import { TaxOverride, WorkOrderTaxOverrideFormValues } from '~/work_order/tax-overrides/types'
import {
  FileUploadState,
  GenericCSVField,
  WORK_ORDER_SPREADSHEET_PRIMARY_KEY
} from '~/bulk_updates/wizard/types/UploadFileTypes'
import createDebugLogger from '~/common/logger'

const debug = createDebugLogger('WorkOrderModifyForm')

/**
 * Kinda do the same thing we do in python where we work through the ranges - combines *contiguous* ranges that
 * have the same values for the provided fields
 * @param edfs
 * @param fields
 *
 * @return start_date, end_date, and properties included in fields
 */
export function groupEffectiveDateRangesByFieldValues(
  edfs: WorkOrderEffectiveDatedFields[],
  fields: string[]
): FilteredEffectiveDatedFields[] {
  const filteredDates = edfs.map(edf =>
    pick(edf, ['start_date', 'end_date'].concat(fields))
  ) as FilteredEffectiveDatedFields[]

  const combine = (
    accum: FilteredEffectiveDatedFields[],
    edf: FilteredEffectiveDatedFields
  ): FilteredEffectiveDatedFields[] => {
    const len = accum.length
    // first item
    if (len === 0) {
      return [edf]
    }
    const last = accum[len - 1]
    if (isEqual(pick(last, fields), pick(edf, fields))) {
      last.end_date = edf.end_date
      return accum
    }
    return [...accum, edf]
  }

  return filteredDates.reduce(combine, [])
}

const RATE_FORM_TO_EDF_FIELD_KEYS = [
  PAY_RATE,
  BILL_RATE,
  MARKUP,
  OVERTIME_MARKUP,
  DOUBLE_TIME_MARKUP,
  MIN_HOURS,
  NOT_QUALIFIED_RATE,
  CALCULATION_PACK,
  WORK_HOURS_PER_DAY
] as const

export type RateFormValueWithEdf = {
  start_date: DateString
  end_date?: DateString
  is_formula_generated?: boolean
  isNew?: boolean
  edf: FilteredEffectiveDatedFields
} & Partial<Record<(typeof RATE_FORM_TO_EDF_FIELD_KEYS)[number], string | number | null>>

export function mapEdfRatesToFormValues(isPayRateMode: boolean) {
  return (allRates: FilteredEffectiveDatedFields[]) =>
    allRates.map(r => ({
      start_date: r.start_date,
      end_date: r.end_date,
      [PAY_RATE]: r.pay_rate?.raw_form_value,
      [BILL_RATE]: r.bill_rate?.raw_form_value,
      // Don't map markup into data unless we are pay rate - let backend calculate for bill rate
      [MARKUP]: isPayRateMode ? r.markup?.raw_form_value : undefined,
      [OVERTIME_MARKUP]: r.overtime_markup?.raw_form_value,
      [DOUBLE_TIME_MARKUP]: r.double_time_markup?.raw_form_value,
      [MIN_HOURS]: r.min_hours?.raw_form_value,
      [NOT_QUALIFIED_RATE]: r.not_qualified_rate?.raw_form_value,
      [CALCULATION_PACK]: r.calculation_pack?.raw_form_value,
      [WORK_HOURS_PER_DAY]: r.work_hours_per_day?.raw_form_value,
      is_formula_generated: r.is_formula_generated,
      edf: r
    }))
}

/** Merges flat rate form values back into the nested EDF shape for read-only display. */
export function formRateValueToEdf(row: RateFormValueWithEdf): WorkOrderEffectiveDatedFields {
  const edf = { ...row.edf, start_date: row.start_date } as WorkOrderEffectiveDatedFields
  if (row.end_date) edf.end_date = row.end_date
  if (row.is_formula_generated != null) edf.is_formula_generated = row.is_formula_generated

  for (const key of RATE_FORM_TO_EDF_FIELD_KEYS) {
    const value = row[key]
    if (value !== undefined && edf[key]) {
      edf[key] = { ...edf[key], raw_form_value: value }
    }
  }
  return edf
}

export function calculateMarkupPercent(bill_rate: string | number, pay_rate: string | number): number {
  return bill_rate && pay_rate && Number(pay_rate) !== 0
    ? Big(bill_rate).div(pay_rate).minus(1).round(10).toNumber()
    : 0
}

type EdfType = WorkOrderEffectiveDatedFields | FilteredEffectiveDatedFields

export function closestEdfOn<T extends EdfType>(edfs: T[], effectiveDate?: DateString): T | null {
  if (!effectiveDate) {
    return null
  }
  const [left, right] = partition(edfs, edf => safeParse(edf.start_date) <= safeParse(effectiveDate))
  if (left.length === 0) {
    return right[0]
  }
  return left[left.length - 1]
}

function writeFiltersForWorkOrderConfig(
  workOrderConfig: WorkOrderConfigResponse
): Partial<Record<FormValuesKey, boolean>> {
  return {
    [PAY_RATE]: workOrderConfig.revise.rates.pay_rate.edit,
    [BILL_RATE]: workOrderConfig.revise.rates.bill_rate.edit,
    [MARKUP]: workOrderConfig.revise.rates.markup.edit,
    [OVERTIME_MARKUP]: workOrderConfig.revise.rates.overtime_markup.edit,
    [DOUBLE_TIME_MARKUP]: workOrderConfig.revise.rates.double_time_markup.edit,
    [MIN_HOURS]: workOrderConfig.revise.rates.min_hours.edit,
    [NOT_QUALIFIED_RATE]: workOrderConfig.revise.rates.not_qualified_rate.edit,
    [CALCULATION_PACK]: workOrderConfig.revise.rates.calculation_pack.edit,
    [WORK_HOURS_PER_DAY]: workOrderConfig.revise.work_hours_per_day.edit,
    [PAY_PARITY_RATE]: workOrderConfig.revise.rates.pay_parity_rate.edit,
    [CALENDAR_PAY_PROFILE]: workOrderConfig.revise.calendar_pay_profile.edit,
    [EXEMPT_STATUS]: workOrderConfig.revise.exempt_status.edit,
    [OVERTIME_PROFILE]: workOrderConfig.revise.overtime_profile.edit,
    [WORK_TYPE_OVERRIDES]: workOrderConfig.revise.work_type_profiles.work_type_override.edit,
    [LOCATION]: workOrderConfig.revise.location.location.edit
  }
}
export function buildChangeSetFromValueArray(
  values: FormValues,
  fieldKey: FormValuesKey,
  subFields: any[],
  workOrderConfig: WorkOrderConfigResponse
): WorkOrderChangeSet[] {
  const newChanges: WorkOrderChangeSet[] = []
  debug.info('In buildChangeSetFromValueArray', { values, fieldKey, subFields })

  // @ts-ignore
  const arrayFieldValue: FormValues[] = values[fieldKey]
  if (!arrayFieldValue) {
    return []
  }

  // This is what is used to not include readonly fields in the changeset (where you will be scolded)
  const writeFilters = writeFiltersForWorkOrderConfig(workOrderConfig)
  arrayFieldValue.forEach((rate: any) => {
    const { start_date, ...rest } = rate
    subFields.forEach(f => {
      const fieldKey: FormValuesKey = f
      if (rest[fieldKey] !== undefined && writeFilters[fieldKey] !== false) {
        newChanges.push({
          action: 'update_field',
          start_date,
          ...valueTransform(fieldKey, rate[fieldKey])
        })
      }
    })
  })
  return newChanges
}
export function buildChangeSetsForFormSection(
  values: FormValues,
  fields: FormValuesKey[],
  effectiveDateField?: FormValuesKey
) {
  const effectiveDate = effectiveDateField && values[effectiveDateField]
  const newChanges: WorkOrderChangeSet[] = []

  debug('buildChangeSetsForFormSection', { values, fields })
  fields.forEach(f => {
    const fieldKey = f
    if (values[fieldKey] !== undefined) {
      newChanges.push({
        action: 'update_field',
        start_date: effectiveDate,
        ...valueTransform(fieldKey, values[fieldKey])
      })
    }
  })
  return newChanges
}

/**
 * Because of the horrible way these values parse into meta_data field in WO, they have to be bundled into
 * the same changeset for the API to apply both changes together to the JSON data
 */
function buildLegacyMetadataFields(values: FormValues) {
  const newChanges: WorkOrderChangeSet[] = []
  if (values.billing_contact !== undefined) {
    newChanges.push({
      action: 'update_field',
      billing_contact: values.billing_contact,
      billing_address: values.billing_address
    })
  }
  return newChanges
}

export function valueTransform(fieldKey: string, fieldValue?: any): Record<string, any> {
  if ([ADDITIONAL_MANAGERS, PROGRAM_TEAM, TIMESHEET_APPROVERS, EXPENSE_APPROVERS, TIMEKEEPER].includes(fieldKey)) {
    // manager fields can be single value in the UI but are always array on the API
    return { [fieldKey]: forceArray(fieldValue) }
  }
  // for shift_section_retained_future_dates, need to map from ShiftApiData[] to shift id array
  if (fieldKey === SHIFTS && Array.isArray(fieldValue) && fieldValue?.[0]?.id !== undefined) {
    return {
      [SHIFTS]: fieldValue?.map(x => x.id)
    }
  }
  // for shift_section_retained_future_dates, need to filter out shift differentials with None or undefined type
  if (fieldKey === SHIFT_DIFFERENTIAL_MAPPING && Array.isArray(fieldValue)) {
    return {
      [SHIFT_DIFFERENTIAL_MAPPING]: fieldValue?.filter(
        s => s.type !== ShiftDifferentialEnum.NONE && s.type !== undefined
      )
    }
  }
  if (fieldKey === WORK_TYPE_OVERRIDES) {
    if (Array.isArray(fieldValue)) {
      return {
        [WORK_TYPE_OVERRIDES]: fieldValue
          ?.filter(x => x != null)
          .map(x => ({ multiplier: x.multiplier, work_type: x.work_type }))
      }
    }
    return {
      [WORK_TYPE_OVERRIDES]: []
    }
  }
  if (fieldKey === MARKUP) {
    let value
    if (typeof fieldValue === 'string') {
      value = fieldValue
    } else {
      // TODO (vic) - what actually sends an object shape to this?
      value = fieldValue?.raw_form_value
    }
    return {
      [MARKUP]: value
    }
  }
  if (fieldKey === BUDGET_ALLOCATIONS) {
    return {
      [BUDGET_ALLOCATIONS]: fieldValue?.map((ba: WorkOrderBudgetAllocations) =>
        omit(ba, ['current_budget_allocation', 'currency_code'])
      )
    }
  }

  return {
    [fieldKey]: fieldValue?.raw_form_value === undefined ? fieldValue : fieldValue.raw_form_value
  }
}

export function buildChangeSetsForKeptEdfs(
  workOrderConfig: WorkOrderConfigResponse,
  values?: FilteredEffectiveDatedFields[]
): WorkOrderChangeSet[] {
  if (!values) {
    return []
  }
  const sortedValues = values.sort((a, b) => {
    if (a.start_date < b.start_date) {
      return -1
    }
    if (a.start_date > b.start_date) {
      return 1
    }
    return 0
  })

  const keptChanges: WorkOrderChangeSet[] = []
  const writeFilters = writeFiltersForWorkOrderConfig(workOrderConfig)

  sortedValues.forEach(edf => {
    for (const [key, value] of Object.entries(omit(edf, ['start_date', 'end_date']))) {
      if (value !== undefined && writeFilters[key as FormValuesKey] !== false) {
        keptChanges.push({
          action: 'update_field',
          start_date: edf.start_date,
          ...valueTransform(key, value)
        })
      }
    }
  })
  return keptChanges
}

export async function transformFieldArrayToRevisePayload(
  fields: GenericCSVField[],
  formValues?: FileUploadState
): Promise<[string, WorkOrderRevisionRequest]> {
  const newChanges = [] as WorkOrderChangeSet[]
  const workOrderId: string = (
    (fields?.find(f => f.key === WORK_ORDER_SPREADSHEET_PRIMARY_KEY)?.value as string) ?? '0'
  ).trim()

  if (workOrderId === '0' || workOrderId === '') {
    return [workOrderId, { changes: [] }]
  }

  const fields_template = await fetchBulkTemplateFields({ includeRestricted: true })

  fields_template.fields.forEach(field_entry => {
    const f_string = fields.find(f => f.key === field_entry.field)?.value
    if (f_string) {
      const change: WorkOrderChangeSet = { action: field_entry.action }
      change[field_entry.field] = f_string
      newChanges.push(change)
    }
  })

  return [
    workOrderId,
    {
      changes: newChanges,
      approval_email: formValues?.requiredApprovals,
      modification_reason: undefined,
      other_reason: formValues?.reasonForUpdate,
      disable_backdating: false,
      connector_data: {}
    }
  ]
}

export function transformFormDataToRevisePayload(
  values: FormValues,
  contractData: WorkOrderCurrentData,
  workOrderConfig: WorkOrderConfigResponse
): WorkOrderRevisionRequest {
  // Create changeset for stuff in the main form area
  let newChanges = [] as WorkOrderChangeSet[]

  if (values.start_date || values.end_date) {
    newChanges.push({ action: 'update_dates', start_date_to: values.start_date, end_date_to: values.end_date })
  }
  if (values.end_time) {
    newChanges.push({ action: 'update_field', end_time: values.end_time, end_timezone: values.end_timezone })
  }

  // new shift section
  newChanges = newChanges.concat(buildChangeSetsForShiftsArray(values, contractData.end_date.raw_form_value))

  // general section
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, GENERAL_SECTION_FIELDS))

  // approvers/managers section
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, APPROVERS_AND_MANAGERS_SECTION_FIELDS))

  // advanced rates section
  newChanges = newChanges.concat(buildChangeSetFromValueArray(values, RATES, RATE_SECTION_FIELDS, workOrderConfig))
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, RATE_SECTION_SINGLE_VALUE_FIELDS))

  // advanced fee profile section
  newChanges = newChanges.concat(
    buildChangeSetFromValueArray(values, FEE_PROFILES, FEE_PROFILE_SECTION_FIELDS, workOrderConfig)
  )

  // basic work type profile section when allow_wtp_wt_effective_dating_and_overriding is off
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, [WORK_TYPE_PROFILE]))

  // advanced location section
  newChanges = newChanges.concat(
    buildChangeSetFromValueArray(values, LOCATIONS, LOCATION_SECTION_FIELDS, workOrderConfig)
  )
  // advanced work type profile section
  newChanges = newChanges.concat(
    buildChangeSetFromValueArray(values, WORK_TYPE_PROFILES, WORK_TYPE_SECTION_FIELDS, workOrderConfig)
  )

  // direct hire field(s) section
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, DIRECT_HIRE_SECTION_FIELDS))

  // generic fields section
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, GENERIC_FIELDS_SECTION_FIELDS))
  // charge codes section
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, CHARGE_CODE_SECTION_FIELDS))
  // org unit sections
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, ORG_UNIT_SECTION_FIELDS))
  // legacy meta_data fields go together
  newChanges = newChanges.concat(buildLegacyMetadataFields(values))

  // location section
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, LOCATION_SECTION_FIELDS))
  // scheduled billing section
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, SCHEDULED_BILLING_SECTION_FIELDS))
  // tax overrides section
  newChanges.push(...buildChangeSetsForTaxOverrides(values, contractData.end_date.raw_form_value))

  // Event-Based Expenses
  newChanges = newChanges.concat(buildChangeSetsForFormSection(values, EVENT_BASED_EXPENSES_SECTION_FIELDS))

  // replay kept things
  debug.info('Applying rate_section_retained_future_dates', values.rate_section_retained_future_dates)
  newChanges = newChanges.concat(buildChangeSetsForKeptEdfs(workOrderConfig, values.rate_section_retained_future_dates))
  newChanges = newChanges.concat(
    buildChangeSetsForKeptEdfs(workOrderConfig, values.fee_profile_section_retained_future_dates)
  )
  newChanges = newChanges.concat(buildChangeSetsForKeptEdfs(workOrderConfig, values.work_type_retained_future_dates))
  if (values.shift_strategy) {
    newChanges = newChanges.concat(
      buildChangeSetsForKeptEdfs(workOrderConfig, values.shift_section_retained_future_dates)
    )
  }

  if (values.payments) {
    newChanges.push({
      action: 'update_payments',
      payments: values.payments
    })
  }
  if (values.budget) {
    newChanges.push({
      action: 'update_budget',
      budget: values.budget
    })
  }

  return {
    changes: [...newChanges],
    modification_reason: values.modification_reason,
    other_reason: values.other_reason,
    disable_backdating: values.disable_backdating,
    connector_data: values.connector_data
  }
}

type MaybeDate = string | null | undefined | number | Date

export function maxDate(dates: MaybeDate[]): Date | undefined {
  const sortedAndFiltered = dates
    .filter(d => Boolean(d))
    .sort((a, b) => safeParse(b!).getTime() - safeParse(a!).getTime())
  return sortedAndFiltered.length > 0 ? safeParse(sortedAndFiltered[0]!) : undefined
}

export function minDate(dates: MaybeDate[]): Date | undefined {
  const sortedAndFiltered = dates
    .filter(d => Boolean(d))
    .sort((a, b) => safeParse(a!).getTime() - safeParse(b!).getTime())
  return sortedAndFiltered.length > 0 ? safeParse(sortedAndFiltered[0]!) : undefined
}

type ValidatorOpts = {
  effectiveDate?: DateString
  woStartDate: DateString
  woStartDayOfWeek: number
  isWoTimekeepingEligible: boolean
  existingMatches: (value: any) => boolean
  errorMessage: string
}

export function onWorkWeekStartDayValidator({
  effectiveDate,
  woStartDate,
  isWoTimekeepingEligible,
  existingMatches,
  woStartDayOfWeek,
  errorMessage
}: ValidatorOpts) {
  return (value: any) => {
    if (effectiveDate) {
      // first see if the user even changed a relevant value
      if (isWoTimekeepingEligible && !existingMatches(value)) {
        // if they did, see if they picked a valid effective date
        return effectiveDate === woStartDate || getISODay(safeParse(effectiveDate)) === woStartDayOfWeek || errorMessage
      }
    }
    return true
  }
}

export const toggleKeptDate = (
  keptEdf: FilteredEffectiveDatedFields,
  kept: boolean,
  keptShift?: ShiftApiData,
  keptDates?: FilteredEffectiveDatedFields[],
  setKeptDates?: any,
  toggleKeptDateBase?: any
) => {
  // VNDLY-32976 for flexible shift strategy, one WOD may have multiple shifts, allow user to delete/revert each shift
  if (keptEdf.shift_strategy?.raw_form_value === ShiftStrategyEnum.FLEXIBLE && keptShift) {
    const shifts = keptDates?.filter((edf: FilteredEffectiveDatedFields) => edf.start_date === keptEdf.start_date)?.[0]
      .shifts
    if (kept) {
      // eslint-disable-next-line no-param-reassign
      keptEdf.shifts = keptEdf.shifts
        ? keptEdf.shifts.filter(s => shifts?.includes(s)).concat([keptShift])
        : [keptShift]
    } else {
      // eslint-disable-next-line no-param-reassign
      keptEdf.shifts = keptEdf.shifts?.filter(s => shifts?.includes(s) && s.id !== keptShift.id)
    }
    setKeptDates(
      keptDates?.filter((edf: FilteredEffectiveDatedFields) => edf.start_date !== keptEdf.start_date).concat([keptEdf])
    )
  } else {
    toggleKeptDateBase(keptEdf, kept)
  }
}

export function transformRawChargeCodesToContextFormat(charge_codes: ChargeCodeRawValues[] | undefined) {
  return charge_codes?.map(cc => cc.fields)
}

export function buildChangeSetsForTaxOverrides(
  values: { tax_override?: TaxOverride[]; end_date?: DateString },
  currentEndDate: DateString
) {
  return buildChangeSetsForDatedOverrides(
    'tax_override',
    values.tax_override,
    values.end_date,
    currentEndDate,
    (taxo: WorkOrderTaxOverrideFormValues) => taxOverrideToChangeSetData(taxo)
  )
}

function buildChangeSetsForShiftsArray(
  values: { shifts_array?: ShiftDatedItem[]; end_date?: DateString },
  currentEndDate: DateString
) {
  if (values.shifts_array == null) {
    return []
  }

  const strategyItems = buildChangeSetsForDatedOverrides(
    'shift_strategy',
    values.shifts_array,
    values.end_date,
    currentEndDate,
    (shift: ShiftDatedItem) => shift.shift_strategy
  )

  const shiftsItems = buildChangeSetsForDatedOverrides(
    'shifts',
    values.shifts_array,
    values.end_date,
    currentEndDate,
    (shift: ShiftDatedItem) => shift.shift_differential_mapping.map(sdm => sdm.shift),
    { emptyVal: [] }
  )

  const shiftDifferentialItems = buildChangeSetsForDatedOverrides(
    'shift_differential_mapping',
    values.shifts_array,
    values.end_date,
    currentEndDate,
    (shift: ShiftDatedItem) => shift.shift_differential_mapping.filter(sd => sd.type !== ShiftDifferentialEnum.NONE),
    { emptyVal: [] }
  )
  return [...strategyItems, ...shiftsItems, ...shiftDifferentialItems]
}

type ChangeSetBuilderOptions = {
  emptyVal: any[] | null
}

/**
 * Common behavior for doing "override ranges" with a list of start/end dated values  - turning it into changesets
 * with start date only
 */
function buildChangeSetsForDatedOverrides(
  value_key: string,
  values: TaxOverride[] | ShiftDatedItem[] | undefined,
  end_date: DateString | undefined,
  currentEndDate: DateString,
  mappingFn: (value: any) => any,
  options: ChangeSetBuilderOptions = { emptyVal: null }
) {
  const newChanges: WorkOrderChangeSet[] = []
  const revisedEndDate = end_date ?? currentEndDate

  if (!values) {
    return []
  }
  // All overrides removed, push global null
  if (!values[0]) {
    newChanges.push({
      action: 'update_field',
      [value_key]: options.emptyVal
    })
  }

  // We index the overrides by start date to prevent duplicate changes for the same day.
  //   The first pass creates end_date+1 null entries
  //   The second pass adds new start dates.
  //   So 2 contiguous ranges could create null + realdate for the same date.  Only keep the realdate one
  const actionDateMapping: Record<DateString, WorkOrderChangeSet> = {}

  // Fill out end date resets first - only create ones that are in the WO start-end range
  values.forEach(lo => {
    if (lo.end_date < revisedEndDate) {
      const loStartDate = Temporal.PlainDate.from(lo.end_date).add({ days: 1 }).toString()
      actionDateMapping[loStartDate] = {
        action: 'update_field',
        start_date: loStartDate,
        [value_key]: options.emptyVal
      }
    }
  })
  // Merge in real start dates for ranges.  May be existing null values from prior "end date" records, replace
  //  those in that case
  values.forEach(lo => {
    actionDateMapping[lo.start_date] = {
      action: 'update_field',
      start_date: lo.start_date,
      [value_key]: mappingFn(lo)
    }
  })
  const vals = Object.values(actionDateMapping)
  debug('buildChangeSetsForDatedOverrides', { incoming_values: values, new_values: vals })
  newChanges.push(...vals)
  return newChanges
}

/**
 *
 * @param workOrderStartDate
 * @param workOrderEndDate
 * @param existingValues
 */

export function adjustContiguousValuesArrayForStartEndDate(
  workOrderStartDate: DateString,
  workOrderEndDate: DateString,
  existingValues: any[]
): any[] {
  let adjustedValues = [...existingValues]
  const firstValidStartIndex = adjustedValues.findLastIndex(r => r.start_date <= workOrderStartDate)
  if (firstValidStartIndex >= 0) {
    adjustedValues = adjustedValues.slice(firstValidStartIndex)
  }

  const beyondEndIndex = adjustedValues.findIndex(r => r.start_date > workOrderEndDate)
  if (beyondEndIndex > 0) {
    // If we are contiguous we have to leave at least 1
    adjustedValues = adjustedValues.slice(0, beyondEndIndex)
  }

  // adjust first item start date to match
  // for contiguous: the first edf must EQUAL the WO start date
  if (adjustedValues.length) {
    debug('adjusting start date', { adjustedValues, workOrderStartDate })
    adjustedValues[0] = { ...adjustedValues[0], start_date: workOrderStartDate }
  }

  return adjustedValues
}

export function adjustNonContiguousValuesArrayForStartEndDate(
  workOrderStartDate: DateString,
  workOrderEndDate: DateString,
  existingValues: any[],
  options: { priorEndDate?: DateString } = {}
): any[] {
  let adjustedValues = [...existingValues]

  // drop any ranges eclipsed by new start date
  const firstValidStartIndex = adjustedValues.findIndex(r => r.end_date && r.end_date >= workOrderStartDate)
  if (firstValidStartIndex >= 0) {
    adjustedValues = adjustedValues.slice(firstValidStartIndex)
  }

  // drop any ranges eclipsed by new end date
  const beyondEndIndex = adjustedValues.findIndex(r => r.start_date > workOrderEndDate)
  if (beyondEndIndex >= 0) {
    adjustedValues = adjustedValues.slice(0, beyondEndIndex)
  }

  if (adjustedValues.length) {
    // adjust first item start date to match
    if (adjustedValues[0].start_date < workOrderStartDate) {
      // two scenarios:
      //   contiguous: the first edf must EQUAL the WO start date
      //   other: the first edf must be ON/AFTER the WO start date
      debug('adjusting start date to match work order start date')
      adjustedValues[0] = { ...adjustedValues[0], start_date: workOrderStartDate }
    }

    const lastIndex = adjustedValues.length - 1
    // if our end date was == wo end date before, it should match the new end date
    if (adjustedValues[lastIndex].end_date && adjustedValues[lastIndex].end_date === options?.priorEndDate) {
      adjustedValues[lastIndex] = { ...adjustedValues[lastIndex], end_date: workOrderEndDate }
    }
    // if our end date was > wo end date before, it should match the new end date
    if (adjustedValues[lastIndex].end_date && adjustedValues[lastIndex].end_date > workOrderEndDate) {
      debug('adjusting end date to match work order end date')
      adjustedValues[lastIndex] = { ...adjustedValues[lastIndex], end_date: workOrderEndDate }
    }
  }

  return adjustedValues
}

interface TemporaryChangeType {
  start_date: string
  end_date: string
  [key: string]: any
}
/**
 * Similiar to `insertRateChangeIntoRatesArray` but for ranges that don't have to cover the entire WO start->end
 */
export function insertTemporaryChangeIntoArray(existingRates: TemporaryChangeType[], newRate: TemporaryChangeType) {
  let currentRates: TemporaryChangeType[] = [...existingRates]
  let updatedRates: TemporaryChangeType[] | null = null
  let updateCount = 0 // to prevent infinite loops

  // We have to run this in a loop because newRate might impact more than one existing range - keep fixing until
  // the list settles out or we hit the limit
  while (!isEqual(updatedRates, currentRates) && updateCount < 1000) {
    debug('insertTemporaryChangeIntoArray', { newRate, currentRates, updatedRates, updateCount })
    currentRates = updatedRates ?? [...currentRates]
    updatedRates = insertTemporaryChangeIntoArrayPass(currentRates, newRate)
    updateCount++
  }
  return currentRates
}

function insertTemporaryChangeIntoArrayPass(existingRates: TemporaryChangeType[], newRate: TemporaryChangeType) {
  const updatedRates = [...existingRates]

  // we assume stuff here can have end dates

  // Eclipsed ranges will be removed
  const eclipsedRangeIndex = updatedRates.findIndex(
    r => r.start_date >= newRate.start_date && r.end_date <= newRate.end_date
  )
  if (eclipsedRangeIndex >= 0) {
    updatedRates.splice(eclipsedRangeIndex, 1)
  }

  const overlappingRangeIndex = updatedRates.findIndex(
    r => r.start_date <= newRate.end_date && r.end_date >= newRate.start_date
  )
  if (overlappingRangeIndex >= 0) {
    let remove = false
    const overlappingRange = { ...updatedRates[overlappingRangeIndex] }

    if (overlappingRange.start_date < newRate.start_date && overlappingRange.end_date > newRate.end_date) {
      // new range is inside existing range, so we split the existing range
      updatedRates.push({
        ...overlappingRange,
        start_date: Temporal.PlainDate.from(newRate.end_date).add({ days: 1 }).toString()
      })
      overlappingRange.end_date = Temporal.PlainDate.from(newRate.start_date).subtract({ days: 1 }).toString()
    }
    if (overlappingRange.start_date < newRate.start_date) {
      // adjust end date for existing range
      overlappingRange.end_date = Temporal.PlainDate.from(newRate.start_date).subtract({ days: 1 }).toString()
    }
    if (overlappingRange.end_date > newRate.end_date) {
      // adjust start date for existing range
      overlappingRange.start_date = Temporal.PlainDate.from(newRate.end_date).add({ days: 1 }).toString()
    }
    if (overlappingRange.end_date <= newRate.end_date && overlappingRange.start_date >= newRate.start_date) {
      // overlapping range is inside the new range, so remove it
      remove = true
    }

    if (remove) {
      updatedRates.splice(overlappingRangeIndex, 1)
    } else {
      updatedRates.splice(overlappingRangeIndex, 1, overlappingRange)
    }
  }

  updatedRates.push(newRate)

  return sortBy(updatedRates, ['start_date'])
}

/**
 * Brain for inserting a rate change into the rate array.  This will split/remove records to comply with the incoming
 * record.  This assumes that there are contiguous ranges in the existing rates array (like rates / fee profiles / etc)
 * @param workOrderEndDate
 * @param existingRates
 * @param newRate
 */
export function insertRateChangeIntoRatesArray(
  workOrderEndDate: DateString,
  existingRates: ContiguousDataRecord[],
  newRate: ContiguousDataRecord
): any[] {
  const updatedRates = [...existingRates]

  // get indices of overlapped existing rates - these will have to be removed
  const touchedIndices = updatedRates.reduce((acc, r, i) => {
    if (r.start_date >= newRate.start_date && r.start_date <= (newRate.end_date ?? workOrderEndDate)) {
      acc.push(i)
    }
    return acc
  }, [] as number[])

  let splitRate
  const newStartDate = newRate.end_date ? Temporal.PlainDate.from(newRate.end_date).add({ days: 1 }).toString() : null
  // split the range that the end date falls within
  if (newStartDate && newStartDate <= workOrderEndDate) {
    // If there is already another rate change for end_date+1, we don't need to insert another
    // But if not, add a revert entry for the value we're inserting in the middle of
    if (!updatedRates.find(r => r.start_date === newStartDate)) {
      const revertRate = updatedRates.findLast(r => r.start_date <= newRate.end_date!)
      splitRate = { ...revertRate, start_date: newStartDate }
    }
  }

  const newRateInsert = { ...omit(newRate, 'end_date', 'in_effect_until'), isNew: true }

  if (touchedIndices.length) {
    if (splitRate) {
      updatedRates.splice(touchedIndices[0], touchedIndices.length, newRateInsert, splitRate as InsertRateFormValue)
    } else {
      // clean insert
      updatedRates.splice(touchedIndices[0], touchedIndices.length, newRateInsert)
    }
  } else {
    if (splitRate) {
      updatedRates.push(splitRate as InsertRateFormValue)
    }
    updatedRates.push(newRateInsert)
  }
  return sortBy(updatedRates, ['start_date'])
}
